import { gql } from "@apollo/client";
import { STANDARDIZED_FUNGIBLE_TOKEN_ANCHOR_FIELDS, STANDARDIZED_PREDICTION_EVENT_NAVIGATION_FIELDS, } from "../graphql/instrumentNavigationFragments.js";
import { SIGNAL_FEED_ACTIVITY_ITEM_FIELDS, SIGNAL_FEED_INSTRUMENT_FIELDS, SIGNAL_FEED_PROFILE_FIELDS, } from "../graphql/signalFeedFragments.js";
export const GetWaitListStatusQuery = gql `
  query GetWaitListStatus($input: GetWaitListStatusRequest!) {
    getWaitListStatus(input: $input) {
      onWaitlist
      position
      referralCode
      referralCount
      referralLink
      status
    }
  }
`;
export const GET_NOTIFICATION_PREFERENCES = gql `
  query GetNotificationPreferencesSummary($input: GetNotificationPreferencesRequest!) {
    getNotificationPreferences(input: $input) {
      categories
    }
  }
`;
export const GetNotificationSettingsQuery = gql `
  query GetNotificationSettings(
    $preferencesInput: GetNotificationPreferencesRequest!
    $alertRulesInput: GetAlertRulesRequest!
  ) {
    getNotificationPreferences(input: $preferencesInput) {
      marketingConsent
      quietHoursEnabled
      quietHoursStart
      quietHoursEnd
      quietHoursTimezone
      categories
    }
    getAlertRules(input: $alertRulesInput) {
      rules {
        id
        alertType
        targetType
        enabled
        cooldownHours
      }
    }
  }
`;
export const GetSupportedPaymentMethodsQuery = gql `
  query GetSupportedPaymentMethods($input: GetSupportedPaymentMethodsRequest!) {
    getSupportedPaymentMethods(input: $input) {
      inputMethods {
        paymentMethod
        platformIds
        title
        subtitle
        icon
        imageUrl
        minimumAllowedAmount {
          currency {
            asset {
              name
              symbol
            }
            decimals
          }
          value
        }
      }
      outputMethods {
        paymentMethod
        platformIds
        title
        subtitle
        icon
        imageUrl
        minimumAllowedAmount {
          currency {
            asset {
              name
              symbol
            }
            decimals
          }
          value
        }
      }
      stablecoin {
        id
        type
        instrument {
          ... on StandardizedFungibleToken {
            id
            standardizedInstrumentId
            currency {
              asset {
                name
                symbol
              }
              decimals
              logoUrl
            }
            fungibleTokens {
              currency {
                asset {
                  name
                  symbol
                }
                decimals
              }
              address
              chainId
              platformId
            }
          }
        }
      }
    }
  }
`;
export const GetRampFormConfigurationQuery = gql `
  query GetRampFormConfiguration($input: GetRampFormConfigurationRequest!) {
    getRampFormConfiguration(input: $input) {
      sections {
        id
        title
        description
        order
        fields {
          id
          type
          label
          placeholder
          helperText
          required
          order
          action
          defaultValue
          options {
            value
            label
            disabled
          }
          validations {
            type
            value
            message
          }
          dependsOn {
            fieldId
            values
          }
        }
      }
      platformId
      direction
      paymentMethod
      supportedCurrencies {
        asset {
          symbol
          name
        }
        decimals
        logoUrl
      }
    }
  }
`;
export const GetStandardizedFungibleTokenCategoriesQuery = gql `
  query GetStandardizedFungibleTokenCategories($input: GetStandardizedFungibleTokenCategoriesRequest!) {
    getStandardizedFungibleTokenCategories(input: $input) {
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
      edges {
        category
        description
        similarFungibleTokens {
          id
          standardizedInstrumentId
          currency {
            asset {
              symbol
              name
            }
            decimals
            logoUrl
          }
          verified
          currentMarketCap {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          currentPrice {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
        }
      }
    }
  }
`;
export const GetStandardizedPerpetualCategoriesQuery = gql `
  query GetStandardizedPerpetualCategories($input: GetStandardizedPerpetualCategoriesRequest!) {
    getStandardizedPerpetualCategories(input: $input) {
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
      edges {
        category
        description
        similarPerps {
          id
          standardizedInstrumentId
          currency {
            asset {
              symbol
              name
            }
            decimals
            logoUrl
          }
          verified
          leverage
          currentPrice {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          currentTotalVolume {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
        }
      }
    }
  }
`;
const PerpTradeFields = gql `
  fragment PerpTradeFields on PerpTrade {
    venue
    symbol
    tradeId
    orderId
    side
    price {
      currency {
        asset {
          symbol
          name
        }
        decimals
        logoUrl
      }
      value
      amount
    }
    size {
      currency {
        asset {
          symbol
          name
        }
        decimals
        logoUrl
      }
      value
      amount
    }
    value {
      currency {
        asset {
          symbol
          name
        }
        decimals
        logoUrl
      }
      value
      amount
    }
    fee {
      currency {
        asset {
          symbol
          name
        }
        decimals
        logoUrl
      }
      value
      amount
    }
    closedPnl {
      currency {
        asset {
          symbol
          name
        }
        decimals
        logoUrl
      }
      value
      amount
    }
    positionBefore {
      currency {
        asset {
          symbol
          name
        }
        decimals
        logoUrl
      }
      value
      amount
    }
    isMaker
    timestamp
    leverage
  }
`;
export const GetUserPerpTradesQuery = gql `
  ${PerpTradeFields}
  query GetUserPerpTrades($input: GetUserPerpTradesRequest!) {
    getUserPerpTrades(input: $input) {
      pageInfo {
        totalCount
        hasNextPage
        hasPreviousPage
      }
      trades {
        ...PerpTradeFields
      }
    }
  }
`;
const TokenTradeFields = gql `
  fragment TokenTradeFields on Trade {
    accountAddress
    amount {
      token {
        currency {
          asset {
            symbol
            name
          }
          decimals
          logoUrl
        }
        address
        chainId
        platformId
      }
      value
      amount
      amountInFiatCurrency {
        currency {
          asset {
            symbol
            name
          }
          decimals
          logoUrl
        }
        value
        amount
      }
    }
    balanceImpact
    fiatPnl {
      currency {
        asset {
          symbol
          name
        }
        decimals
        logoUrl
      }
      value
      amount
    }
    timestamp
  }
`;
export const GetUserTokenTradesQuery = gql `
  ${TokenTradeFields}
  query GetUserTokenTrades($input: GetUserTokenTradesRequest!) {
    getUserTokenTrades(input: $input) {
      pageInfo {
        totalCount
        hasNextPage
        hasPreviousPage
      }
      trades {
        ...TokenTradeFields
      }
    }
  }
`;
const PredictionOutcomePositionFields = gql `
  fragment PredictionOutcomePositionFields on PredictionOutcomePosition {
    standardizedInstrumentId
    avgEntryPrice
    eventTitle
    outcomeIndex
    pnl {
      amount
      value
      currency {
        asset {
          symbol
          name
        }
        decimals
        logoUrl
      }
    }
    pnlPercent
    size
    market {
      question
      imageUrl
      groupItemTitle
      status
      outcomes {
        label
        color
        externalTokenId
        price {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
      }
    }
  }
`;
export const GetUserPredictionEventPositionsQuery = gql `
  ${PredictionOutcomePositionFields}
  query GetUserPredictionEventPositions($input: GetUserPredictionEventPositionsRequest!) {
    getUserPredictionEventPositions(input: $input) {
      pageInfo {
        totalCount
        hasNextPage
        hasPreviousPage
      }
      positions {
        ...PredictionOutcomePositionFields
      }
    }
  }
`;
export const GetStandardizedPredictionEventCategoriesQuery = gql `
  query GetStandardizedPredictionEventCategories($input: GetStandardizedPredictionEventCategoriesRequest!) {
    getStandardizedPredictionEventCategories(input: $input) {
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
      edges {
        category
        description
        similarPredictions {
          id
          standardizedInstrumentId
          slug
          title
          imageUrl
          closesAt
          volume {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          isSeries
          eventType
          sportMetadata {
            isLive
            gameStatus
            sportType
            league
            scheduledStartTime
            homeTeamScore
            awayTeamScore
            period
            elapsed
            homeTeam {
              name
              abbreviation
              logo
              color
              record
            }
            awayTeam {
              name
              abbreviation
              logo
              color
              record
            }
          }
          markets {
            question
            description
            resolutionSource
            imageUrl
            groupItemTitle
            sportsMarketType
            startDate
            endDate
            status
            volume {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            outcomes {
              externalTokenId
              price {
                start {
                  timestamp
                  value
                }
                end {
                  timestamp
                  value
                }
                changeInPercentage
              }
              # imageUrl
              label
              # abbreviation
              color
              # teamMetadata {
              #   name
              #   abbreviation
              #   logo
              #   color
              #   record
              # }
            }
          }
        }
      }
    }
  }
`;
export const GetCategoriesQuery = gql `
  query GetCategories($input: GetCategoriesRequest!) {
    getCategories(input: $input) {
      homeFilters {
        fungibleTokens {
          id
          title
          subcategories {
            id
            title
            clientCacheKey
            filterGroups {
              id
              title
              imageUrl
              layout
              filters {
                id
                title
                imageUrl
              }
            }
          }
        }
        perps {
          id
          title
          subcategories {
            id
            title
            clientCacheKey
            filterGroups {
              id
              title
              layout
              imageUrl
              filters {
                id
                title
                imageUrl
              }
            }
          }
        }
        predictions {
          id
          title
          subcategories {
            id
            title
            clientCacheKey
            filterGroups {
              id
              title
              imageUrl
              layout
              filters {
                id
                title
                imageUrl
              }
            }
          }
        }
      }
      searchFilters {
        fungibleTokens {
          typeFilters {
            id
            title
            isDefault
          }
          timeFilters {
            id
            title
            timeInterval {
              amount
              unit
            }
            isDefault
          }
          categoryFilters {
            id
            title
            isDefault
          }
        }
        perps {
          typeFilters {
            id
            title
            isDefault
          }
          timeFilters {
            id
            title
            timeInterval {
              amount
              unit
            }
            isDefault
          }
          categoryFilters {
            id
            title
            isDefault
          }
        }
        predictions {
          typeFilters {
            id
            title
            isDefault
          }
          timeFilters {
            id
            title
            timeInterval {
              amount
              unit
            }
            isDefault
          }
          categoryFilters {
            id
            title
            isDefault
          }
        }
      }
    }
  }
`;
export const GetStandardizedFungibleTokenQuery = gql `
  query GetStandardizedFungibleTokenQuery($input: GetStandardizedFungibleTokenRequest!) {
    getStandardizedFungibleToken(input: $input) {
      id
      standardizedInstrumentId
      associatedPerpStandardizedInstrumentId
      verified
      currency {
        asset {
          symbol
          name
        }
        decimals
        logoUrl
      }
      currentMarketCap {
        start {
          timestamp
          value
        }
        end {
          timestamp
          value
        }
        changeInPercentage
      }
      currentPrice {
        start {
          timestamp
          value
        }
        end {
          timestamp
          value
        }
        changeInPercentage
      }
      currentTotalVolume {
        start {
          timestamp
          value
        }
        end {
          timestamp
          value
        }
        changeInPercentage
      }
      currency {
        asset {
          symbol
          name
        }
        decimals
        logoUrl
      }
      marketData {
        chartData {
          fiveMinutes {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
          oneHour {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
          fourHours {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
          oneDay {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
          sevenDays {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
          oneMonth {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
          sixMonths {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
          oneYear {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
          all {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
        }
        circulatingSupply {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
        recentTrades {
          accountAddress
          amount {
            token {
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
              address
              chainId
              platformId
            }
            value
            amount
            amountInFiatCurrency {
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
              value
              amount
            }
          }
          balanceImpact
          timestamp
        }
        totalHolders {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
        top10HoldersPercentage
      }
      metadata {
        socials {
          platform
          url
          imageUrl
        }
        description
        createdAt
        launchpad
      }
      userMetadata {
        isWatching
        currentBalance {
          currency {
            asset {
              symbol
              name
            }
            decimals
            logoUrl
          }
          value
          amount
          amountInFiatCurrency {
            currency {
              asset {
                symbol
                name
              }
              decimals
            }
            value
            amount
          }
        }
        valueHistory {
          value {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          valueChanges {
            fiveMinutes {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            oneHour {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            fourHours {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            oneDay {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            sevenDays {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            oneMonth {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            sixMonths {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            oneYear {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            all {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
          }
        }
      }
    }
  }
`;
export const GetStandardizedPerpetualQuery = gql `
  query GetStandardizedPerpetualQuery($input: GetStandardizedPerpetualRequest!) {
    getStandardizedPerpetual(input: $input) {
      id
      standardizedInstrumentId
      verified
      leverage
      currency {
        asset {
          symbol
          name
        }
        decimals
        logoUrl
      }
      currentPrice {
        start {
          timestamp
          value
        }
        end {
          timestamp
          value
        }
        changeInPercentage
      }
      oraclePrice {
        start {
          timestamp
          value
        }
        end {
          timestamp
          value
        }
        changeInPercentage
      }
      currentTotalVolume {
        start {
          timestamp
          value
        }
        end {
          timestamp
          value
        }
        changeInPercentage
      }
      metadata {
        socials {
          platform
          url
          imageUrl
        }
        description
      }
      markets {
        venue
        leverage
        tradingData {
          positionSize {
            over5M {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between2_5MAnd5M {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between1MAnd2_5M {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between500KAnd1M {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between250KAnd500K {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between100KAnd250K {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between50KAnd100K {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between25KAnd50K {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between10KAnd25K {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between1KAnd10K {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between250And1K {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            under250 {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            total {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
          }
          totalPnl {
            over1M {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between100KAnd1M {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between10KAnd100K {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between0And10K {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            betweenNeg10KAnd0 {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            betweenNeg100KAndNeg10K {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            underNeg100K {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            total {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
          }
          accountSize {
            over5M {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between1MAnd5M {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between500KAnd1M {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between100KAnd500K {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between50KAnd100K {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between10KAnd50K {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            between250And10K {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            under250 {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
            total {
              count
              longsNotionalUsd
              shortsNotionalUsd
              longsCount
              shortsCount
            }
          }
        }
        fundingRate {
          rate {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          timestamp
        }
        openInterest {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
        chartData {
          fiveMinutes {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
          oneHour {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
          fourHours {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
          oneDay {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
          sevenDays {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
          oneMonth {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
          sixMonths {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
          oneYear {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
          all {
            price {
              timestamp
              value
            }
            priceOHLC {
              timestamp
              open
              high
              low
              close
            }
            priceChangeInPercentage
            summary {
              sells {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
              buys {
                numberOfTransactions
                totalVolume
                numberOfUniqueAccounts
              }
            }
          }
        }
        recentTrades {
          venue
          symbol
          tradeId
          orderId
          side
          price {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          size {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          value {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          fee {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          closedPnl {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          positionBefore {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          isMaker
          timestamp
        }
      }
      userMetadata {
        positions {
          side
          size {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
            amountInFiatCurrency {
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
              value
              amount
            }
          }
          entryPrice {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          notionalUsd {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          unrealizedPnlUsd {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          cumulativeFundingUsd {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          leverage
          liquidationPrice {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          marginUsed {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          returnOnEquity
        }
        isWatching
      }
    }
  }
`;
/**
 * Lightweight version of GetStandardizedFungibleTokenQuery that omits chartData
 * from marketData. Used by the web app which fetches chart data independently
 * via GetTokenChartDataQuery for better performance (1 ClickHouse query per
 * timeframe instead of 8 parallel queries upfront).
 */
export const GetStandardizedFungibleTokenLightQuery = gql `
  query GetStandardizedFungibleTokenLightQuery($input: GetStandardizedFungibleTokenRequest!) {
    getStandardizedFungibleToken(input: $input) {
      id
      standardizedInstrumentId
      associatedPerpStandardizedInstrumentId
      verified
      currency {
        asset {
          symbol
          name
        }
        decimals
        logoUrl
      }
      currentMarketCap {
        start {
          timestamp
          value
        }
        end {
          timestamp
          value
        }
        changeInPercentage
      }
      currentPrice {
        start {
          timestamp
          value
        }
        end {
          timestamp
          value
        }
        changeInPercentage
      }
      currentTotalVolume {
        start {
          timestamp
          value
        }
        end {
          timestamp
          value
        }
        changeInPercentage
      }
      currency {
        asset {
          symbol
          name
        }
        decimals
        logoUrl
      }
      recentTrades {
        accountAddress
        amount {
          token {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            address
            chainId
            platformId
          }
          value
          amount
          amountInFiatCurrency {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
        }
        balanceImpact
        timestamp
      }
      metadata {
        socials {
          platform
          url
          imageUrl
        }
        description
        createdAt
        launchpad
        holders
        top10HoldersPercent
      }
      anchorToken {
        currency {
          asset {
            symbol
            name
          }
          decimals
          logoUrl
        }
        address
        chainId
        platformId
      }
      userMetadata {
        isWatching
        currentBalance {
          currency {
            asset {
              symbol
              name
            }
            decimals
            logoUrl
          }
          value
          amount
          amountInFiatCurrency {
            currency {
              asset {
                symbol
                name
              }
              decimals
            }
            value
            amount
          }
        }
        valueHistory {
          value {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          valueChanges {
            fiveMinutes {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            oneHour {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            fourHours {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            oneDay {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            sevenDays {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            oneMonth {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            sixMonths {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            oneYear {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            all {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
          }
        }
      }
    }
  }
`;
export const GetTokenChartDataQuery = gql `
  query GetTokenChartDataQuery($input: GetTokenChartDataRequest!) {
    getTokenChartData(input: $input) {
      timeframe
      data {
        price {
          timestamp
          value
        }
        priceOHLC {
          timestamp
          open
          high
          low
          close
        }
        priceChangeInPercentage
        summary {
          sells {
            numberOfTransactions
            totalVolume
            numberOfUniqueAccounts
          }
          buys {
            numberOfTransactions
            totalVolume
            numberOfUniqueAccounts
          }
        }
      }
    }
  }
`;
/**
 * Lightweight version of GetStandardizedPerpetualQuery that omits chartData
 * from markets. Used by the web app which fetches chart data independently
 * via GetPerpChartDataQuery for better performance (1 API call per timeframe
 * instead of 8 parallel calls upfront).
 */
export const GetStandardizedPerpetualLightQuery = gql `
  query GetStandardizedPerpetualLightQuery($input: GetStandardizedPerpetualRequest!) {
    getStandardizedPerpetual(input: $input) {
      id
      standardizedInstrumentId
      verified
      leverage
      currency {
        asset {
          symbol
          name
        }
        decimals
        logoUrl
      }
      currentPrice {
        start {
          timestamp
          value
        }
        end {
          timestamp
          value
        }
        changeInPercentage
      }
      oraclePrice {
        start {
          timestamp
          value
        }
        end {
          timestamp
          value
        }
        changeInPercentage
      }
      currentTotalVolume {
        start {
          timestamp
          value
        }
        end {
          timestamp
          value
        }
        changeInPercentage
      }
      metadata {
        socials {
          platform
          url
          imageUrl
        }
        description
      }
      markets {
        venue
        leverage
        fundingRate {
          rate {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          timestamp
        }
        openInterest {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
        recentTrades {
          venue
          symbol
          tradeId
          orderId
          side
          price {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          size {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          value {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          fee {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          closedPnl {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          positionBefore {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          isMaker
          timestamp
        }
      }
      userMetadata {
        positions {
          side
          size {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
            amountInFiatCurrency {
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
              value
              amount
            }
          }
          entryPrice {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          notionalUsd {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          unrealizedPnlUsd {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          cumulativeFundingUsd {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          leverage
          liquidationPrice {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          marginUsed {
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
            value
            amount
          }
          returnOnEquity
        }
        isWatching
      }
    }
  }
`;
export const GetPerpChartDataQuery = gql `
  query GetPerpChartDataQuery($input: GetPerpChartDataRequest!) {
    getPerpChartData(input: $input) {
      timeframe
      data {
        price {
          timestamp
          value
        }
        priceOHLC {
          timestamp
          open
          high
          low
          close
        }
        priceChangeInPercentage
      }
    }
  }
`;
// OHLC bars at a resolution (bar size) over an arbitrary [fromSec, toSec]
// window — the TradingView datafeed getBars contract (scroll/zoom/paginate).
// Returns the same MarketDataTimeSeriesAndSummary shape as the per-timeframe
// queries, so the existing chart transforms consume it unchanged.
export const GetChartBarsQuery = gql `
  query GetChartBarsQuery($input: GetChartBarsRequest!) {
    getChartBars(input: $input) {
      data {
        price {
          timestamp
          value
        }
        priceOHLC {
          timestamp
          open
          high
          low
          close
        }
        priceChangeInPercentage
      }
    }
  }
`;
export const GetStandardizedPredictionEventQuery = gql `
  query GetStandardizedPredictionEventQuery($input: GetStandardizedPredictionEventRequest!) {
    getStandardizedPredictionEvent(input: $input) {
      id
      standardizedInstrumentId
      slug
      title
      closesAt
      description
      imageUrl
      volume {
        start {
          timestamp
          value
        }
        end {
          timestamp
          value
        }
        changeInPercentage
      }
      eventType
      isSeries
      sportMetadata {
        isLive
        gameStatus
        sportType
        league
        scheduledStartTime
        homeTeamScore
        awayTeamScore
        period
        elapsed
        homeTeam {
          name
          abbreviation
          league
          logo
          color
          record
        }
        awayTeam {
          name
          abbreviation
          league
          logo
          color
          record
        }
        homeTeamWin {
          externalTokenId
          label
          color
          price {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          chartData {
            __typename
            oneHour {
              price {
                timestamp
                value
              }
              priceOHLC {
                timestamp
                open
                high
                low
                close
              }
              priceChangeInPercentage
              summary {
                sells {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
                buys {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
              }
            }
            oneDay {
              price {
                timestamp
                value
              }
              priceOHLC {
                timestamp
                open
                high
                low
                close
              }
              priceChangeInPercentage
              summary {
                sells {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
                buys {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
              }
            }
            all {
              price {
                timestamp
                value
              }
              priceOHLC {
                timestamp
                open
                high
                low
                close
              }
              priceChangeInPercentage
              summary {
                sells {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
                buys {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
              }
            }
          }
        }
        awayTeamWin {
          externalTokenId
          label
          color
          price {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          chartData {
            __typename
            oneHour {
              price {
                timestamp
                value
              }
              priceOHLC {
                timestamp
                open
                high
                low
                close
              }
              priceChangeInPercentage
              summary {
                sells {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
                buys {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
              }
            }
            oneDay {
              price {
                timestamp
                value
              }
              priceOHLC {
                timestamp
                open
                high
                low
                close
              }
              priceChangeInPercentage
              summary {
                sells {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
                buys {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
              }
            }
            all {
              price {
                timestamp
                value
              }
              priceOHLC {
                timestamp
                open
                high
                low
                close
              }
              priceChangeInPercentage
              summary {
                sells {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
                buys {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
              }
            }
          }
        }
        draw {
          externalTokenId
          label
          color
          price {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          chartData {
            __typename
            oneHour {
              price {
                timestamp
                value
              }
              priceOHLC {
                timestamp
                open
                high
                low
                close
              }
              priceChangeInPercentage
              summary {
                sells {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
                buys {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
              }
            }
            oneDay {
              price {
                timestamp
                value
              }
              priceOHLC {
                timestamp
                open
                high
                low
                close
              }
              priceChangeInPercentage
              summary {
                sells {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
                buys {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
              }
            }
            all {
              price {
                timestamp
                value
              }
              priceOHLC {
                timestamp
                open
                high
                low
                close
              }
              priceChangeInPercentage
              summary {
                sells {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
                buys {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
              }
            }
          }
        }
      }
      markets {
        question
        description
        resolutionSource
        imageUrl
        conditionId
        groupItemTitle
        sportsMarketType
        startDate
        endDate
        status
        volume {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
        outcomes {
          externalTokenId
          price {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          # imageUrl
          label
          # abbreviation
          color
          # teamMetadata {
          #   name
          #   abbreviation
          #   league
          #   logo
          #   color
          #   record
          # }
          chartData {
            __typename
            # fiveMinutes {
            #   price {
            #     timestamp
            #     value
            #   }
            #   priceOHLC {
            #     timestamp
            #     open
            #     high
            #     low
            #     close
            #   }
            #   priceChangeInPercentage
            #   summary {
            #     sells {
            #       numberOfTransactions
            #       totalVolume
            #       numberOfUniqueAccounts
            #     }
            #     buys {
            #       numberOfTransactions
            #       totalVolume
            #       numberOfUniqueAccounts
            #     }
            #   }
            # }
            oneHour {
              price {
                timestamp
                value
              }
              priceOHLC {
                timestamp
                open
                high
                low
                close
              }
              priceChangeInPercentage
              summary {
                sells {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
                buys {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
              }
            }
            # fourHours {
            #   price {
            #     timestamp
            #     value
            #   }
            #   priceOHLC {
            #     timestamp
            #     open
            #     high
            #     low
            #     close
            #   }
            #   priceChangeInPercentage
            #   summary {
            #     sells {
            #       numberOfTransactions
            #       totalVolume
            #       numberOfUniqueAccounts
            #     }
            #     buys {
            #       numberOfTransactions
            #       totalVolume
            #       numberOfUniqueAccounts
            #     }
            #   }
            # }
            oneDay {
              price {
                timestamp
                value
              }
              priceOHLC {
                timestamp
                open
                high
                low
                close
              }
              priceChangeInPercentage
              summary {
                sells {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
                buys {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
              }
            }
            sevenDays {
              price {
                timestamp
                value
              }
              priceOHLC {
                timestamp
                open
                high
                low
                close
              }
              priceChangeInPercentage
              summary {
                sells {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
                buys {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
              }
            }
            oneMonth {
              price {
                timestamp
                value
              }
              priceOHLC {
                timestamp
                open
                high
                low
                close
              }
              priceChangeInPercentage
              summary {
                sells {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
                buys {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
              }
            }
            # sixMonths {
            #   price {
            #     timestamp
            #     value
            #   }
            #   priceOHLC {
            #     timestamp
            #     open
            #     high
            #     low
            #     close
            #   }
            #   priceChangeInPercentage
            #   summary {
            #     sells {
            #       numberOfTransactions
            #       totalVolume
            #       numberOfUniqueAccounts
            #     }
            #     buys {
            #       numberOfTransactions
            #       totalVolume
            #       numberOfUniqueAccounts
            #     }
            #   }
            # }
            # oneYear {
            #   price {
            #     timestamp
            #     value
            #   }
            #   priceOHLC {
            #     timestamp
            #     open
            #     high
            #     low
            #     close
            #   }
            #   priceChangeInPercentage
            #   summary {
            #     sells {
            #       numberOfTransactions
            #       totalVolume
            #       numberOfUniqueAccounts
            #     }
            #     buys {
            #       numberOfTransactions
            #       totalVolume
            #       numberOfUniqueAccounts
            #     }
            #   }
            # }
            all {
              price {
                timestamp
                value
              }
              priceOHLC {
                timestamp
                open
                high
                low
                close
              }
              priceChangeInPercentage
              summary {
                sells {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
                buys {
                  numberOfTransactions
                  totalVolume
                  numberOfUniqueAccounts
                }
              }
            }
          }
        }
      }
      categories {
        category
        description
      }
      userMetadata {
        isWatching
      }
      series {
        id
        standardizedInstrumentId
        slug
        title
        closesAt
      }
      sportDisplaySections {
        title
        defaultExpanded
        content {
          baseTitle
          layoutType
          appendValueToTitle
          valueOptions {
            value
            market {
              question
              imageUrl
              groupItemTitle
              sportsMarketType
              startDate
              endDate
              status
              volume {
                start {
                  timestamp
                  value
                }
                end {
                  timestamp
                  value
                }
                changeInPercentage
              }
              outcomes {
                externalTokenId
                price {
                  start {
                    timestamp
                    value
                  }
                  end {
                    timestamp
                    value
                  }
                  changeInPercentage
                }
                label
                color
              }
            }
          }
        }
      }
    }
  }
`;
/**
 * Lightweight version of GetStandardizedPredictionEventQuery.
 * Requests only the fields already cached by the list query (ListStandardizedInstrumentsByCategoryQuery),
 * so Apollo's cache-first policy can resolve it instantly without a network request when navigating
 * from the prediction list. Chart data is fetched separately via GetPredictionChartDataQuery.
 *
 * Detail-only data (description, categories, userMetadata, series, sportDisplaySections)
 * is omitted here — it loads via the bottom tabs or secondary queries as needed.
 */
export const GetStandardizedPredictionEventLightQuery = gql `
  query GetStandardizedPredictionEventLightQuery($input: GetStandardizedPredictionEventRequest!) {
    getStandardizedPredictionEvent(input: $input) {
      id
      standardizedInstrumentId
      slug
      title
      closesAt
      imageUrl
      volume {
        end {
          timestamp
          value
        }
        changeInPercentage
      }
      eventType
      isSeries
      sportMetadata {
        isLive
        gameStatus
        sportType
        league
        scheduledStartTime
        homeTeamScore
        awayTeamScore
        period
        elapsed
        homeTeam {
          name
          abbreviation
          logo
          color
          record
        }
        awayTeam {
          name
          abbreviation
          logo
          color
          record
        }
        homeTeamWin {
          externalTokenId
          label
          color
          price {
            end {
              timestamp
              value
            }
          }
        }
        awayTeamWin {
          externalTokenId
          label
          color
          price {
            end {
              timestamp
              value
            }
          }
        }
        draw {
          externalTokenId
          label
          color
          price {
            end {
              timestamp
              value
            }
          }
        }
      }
      markets {
        question
        imageUrl
        conditionId
        groupItemTitle
        sportsMarketType
        volume {
          end {
            timestamp
            value
          }
        }
        outcomes {
          externalTokenId
          price {
            end {
              timestamp
              value
            }
          }
          label
          color
        }
      }
    }
  }
`;
/**
 * Mobile-optimised variant of GetStandardizedPredictionEventQuery.
 * Includes all detail fields the mobile app needs (series, sportDisplaySections,
 * userMetadata, market status/description, categories) but omits the heavy
 * chartData blocks on outcomes and sportMetadata outcomes.
 * Chart data is fetched lazily per-timeframe via GetPredictionChartDataQuery,
 * matching the web app's two-stage pattern.
 * Uses cache-first so navigation from the prediction list renders instantly
 * from Apollo's normalised cache.
 */
export const GetStandardizedPredictionEventMobileQuery = gql `
  query GetStandardizedPredictionEventMobileQuery($input: GetStandardizedPredictionEventRequest!) {
    getStandardizedPredictionEvent(input: $input) {
      id
      standardizedInstrumentId
      slug
      title
      closesAt
      description
      imageUrl
      volume {
        start {
          timestamp
          value
        }
        end {
          timestamp
          value
        }
        changeInPercentage
      }
      eventType
      isSeries
      sportMetadata {
        isLive
        gameStatus
        sportType
        league
        scheduledStartTime
        homeTeamScore
        awayTeamScore
        period
        elapsed
        homeTeam {
          name
          abbreviation
          league
          logo
          color
          record
        }
        awayTeam {
          name
          abbreviation
          league
          logo
          color
          record
        }
        homeTeamWin {
          externalTokenId
          label
          color
          price {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
        }
        awayTeamWin {
          externalTokenId
          label
          color
          price {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
        }
        draw {
          externalTokenId
          label
          color
          price {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
        }
      }
      markets {
        question
        description
        resolutionSource
        imageUrl
        conditionId
        groupItemTitle
        sportsMarketType
        startDate
        endDate
        status
        volume {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
        outcomes {
          externalTokenId
          price {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          label
          color
        }
      }
      categories {
        category
        description
      }
      userMetadata {
        isWatching
      }
      series {
        id
        standardizedInstrumentId
        slug
        title
        closesAt
      }
      sportDisplaySections {
        title
        defaultExpanded
        content {
          baseTitle
          layoutType
          appendValueToTitle
          valueOptions {
            value
            market {
              question
              imageUrl
              groupItemTitle
              sportsMarketType
              startDate
              endDate
              status
              volume {
                start {
                  timestamp
                  value
                }
                end {
                  timestamp
                  value
                }
                changeInPercentage
              }
              outcomes {
                externalTokenId
                price {
                  start {
                    timestamp
                    value
                  }
                  end {
                    timestamp
                    value
                  }
                  changeInPercentage
                }
                label
                color
              }
            }
          }
        }
      }
    }
  }
`;
export const GetPredictionChartDataQuery = gql `
  query GetPredictionChartDataQuery($input: GetPredictionChartDataRequest!) {
    getPredictionChartData(input: $input) {
      timeframe
      outcomes {
        outcomeTokenId
        data {
          price {
            timestamp
            value
          }
          priceOHLC {
            timestamp
            open
            high
            low
            close
          }
          priceChangeInPercentage
          summary {
            sells {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
            buys {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
          }
        }
      }
    }
  }
`;
/**
 * Fetches only sportDisplaySections for a prediction event (Moneyline / Spreads / Totals).
 * Used lazily by the Markets tab on sports events. Does not request markets or chartData,
 * so the resolver skips the expensive chart and market data fetches.
 */
export const GetSportDisplaySectionsQuery = gql `
  query GetSportDisplaySectionsQuery($input: GetStandardizedPredictionEventRequest!) {
    getStandardizedPredictionEvent(input: $input) {
      id
      standardizedInstrumentId
      slug
      sportDisplaySections {
        title
        defaultExpanded
        content {
          baseTitle
          layoutType
          appendValueToTitle
          valueOptions {
            value
            market {
              question
              groupItemTitle
              sportsMarketType
              volume {
                end {
                  timestamp
                  value
                }
              }
              outcomes {
                externalTokenId
                price {
                  end {
                    timestamp
                    value
                  }
                }
                label
                color
              }
            }
          }
        }
      }
    }
  }
`;
export const ListStandardizedInstrumentsByCategoryQuery = gql `
  query ListStandardizedInstrumentsByCategoryQuery($input: ListStandardizedInstrumentsByCategoryRequest!) {
    listStandardizedInstrumentsByCategory(input: $input) {
      hotTokens {
        pageInfo {
          hasNextPage
          hasPreviousPage
        }
        edges {
          id
          type
          hotScore
          trades1h
          hotSide
          hotBuy
          hotOutcomeLabel
          hotMarketQuestion
          hotConditionId
          hotOutcomeTokenId
          instrument {
            ... on StandardizedFungibleToken {
              id
              standardizedInstrumentId
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
              verified
              currentPrice {
                start {
                  timestamp
                  value
                }
                end {
                  timestamp
                  value
                }
                changeInPercentage
              }
              currentMarketCap {
                start {
                  timestamp
                  value
                }
                end {
                  timestamp
                  value
                }
                changeInPercentage
              }
              ...StandardizedFungibleTokenAnchorFields
            }
            ... on StandardizedPerpetual {
              id
              standardizedInstrumentId
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
              verified
              currentPrice {
                start {
                  timestamp
                  value
                }
                end {
                  timestamp
                  value
                }
                changeInPercentage
              }
              currentTotalVolume {
                start {
                  timestamp
                  value
                }
                end {
                  timestamp
                  value
                }
                changeInPercentage
              }
              leverage
            }
            ... on StandardizedPredictionEvent {
              id
              standardizedInstrumentId
              ...StandardizedPredictionEventNavigationFields
              title
              imageUrl
              volume {
                end {
                  timestamp
                  value
                }
                changeInPercentage
              }
              closesAt
              eventType
              isSeries
              sportMetadata {
                isLive
                gameStatus
                sportType
                league
                scheduledStartTime
                homeTeamScore
                awayTeamScore
                period
                elapsed
                homeTeam {
                  name
                  abbreviation
                  logo
                  color
                  record
                }
                awayTeam {
                  name
                  abbreviation
                  logo
                  color
                  record
                }
                homeTeamWin {
                  externalTokenId
                  label
                  color
                  price {
                    end {
                      timestamp
                      value
                    }
                  }
                }
                awayTeamWin {
                  externalTokenId
                  label
                  color
                  price {
                    end {
                      timestamp
                      value
                    }
                  }
                }
                draw {
                  externalTokenId
                  label
                  color
                  price {
                    end {
                      timestamp
                      value
                    }
                  }
                }
              }
              markets {
                conditionId
                endDate
                question
                imageUrl
                groupItemTitle
                sportsMarketType
                volume {
                  end {
                    timestamp
                    value
                  }
                }
                outcomes {
                  externalTokenId
                  price {
                    end {
                      timestamp
                      value
                    }
                  }
                  label
                  color
                }
              }
            }
          }
        }
      }
      watchList {
        pageInfo {
          hasNextPage
          hasPreviousPage
        }
        edges {
          id
          type
          instrument {
            ... on StandardizedFungibleToken {
              id
              standardizedInstrumentId
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
              verified
              currentPrice {
                start {
                  timestamp
                  value
                }
                end {
                  timestamp
                  value
                }
                changeInPercentage
              }
              currentMarketCap {
                start {
                  timestamp
                  value
                }
                end {
                  timestamp
                  value
                }
                changeInPercentage
              }
              ...StandardizedFungibleTokenAnchorFields
            }
            ... on StandardizedPerpetual {
              id
              standardizedInstrumentId
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
              verified
              currentPrice {
                start {
                  timestamp
                  value
                }
                end {
                  timestamp
                  value
                }
                changeInPercentage
              }
              currentTotalVolume {
                start {
                  timestamp
                  value
                }
                end {
                  timestamp
                  value
                }
                changeInPercentage
              }
              leverage
            }
            ... on StandardizedPredictionEvent {
              id
              standardizedInstrumentId
              ...StandardizedPredictionEventNavigationFields
              title
              imageUrl
              volume {
                end {
                  timestamp
                  value
                }
                changeInPercentage
              }
              closesAt
              eventType
              isSeries
              sportMetadata {
                isLive
                gameStatus
                sportType
                league
                scheduledStartTime
                homeTeamScore
                awayTeamScore
                period
                elapsed
                homeTeam {
                  name
                  abbreviation
                  logo
                  color
                  record
                }
                awayTeam {
                  name
                  abbreviation
                  logo
                  color
                  record
                }
                homeTeamWin {
                  externalTokenId
                  label
                  color
                  price {
                    end {
                      timestamp
                      value
                    }
                  }
                }
                awayTeamWin {
                  externalTokenId
                  label
                  color
                  price {
                    end {
                      timestamp
                      value
                    }
                  }
                }
                draw {
                  externalTokenId
                  label
                  color
                  price {
                    end {
                      timestamp
                      value
                    }
                  }
                }
              }
              markets {
                conditionId
                endDate
                question
                imageUrl
                groupItemTitle
                sportsMarketType
                volume {
                  end {
                    timestamp
                    value
                  }
                }
                outcomes {
                  externalTokenId
                  price {
                    end {
                      timestamp
                      value
                    }
                  }
                  label
                  color
                }
              }
            }
          }
        }
      }
      standardizedFungibleTokensByCategory {
        category
        marketType
        pageInfo {
          hasNextPage
          hasPreviousPage
        }
        edges {
          id
          standardizedInstrumentId
          currency {
            asset {
              symbol
              name
            }
            decimals
            logoUrl
          }
          verified
          anchorToken {
            address
            platformId
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
          }
          metadata {
            socials {
              platform
              url
            }
            description
            createdAt
            launchpad
            lifecycleStage
            bondingCurveProgress
            holders
            txCount
            uniqueTraders
            confidenceScore
            devHoldingPercent
            sniperHoldingPercent
            top10HoldersPercent
            bundleHoldingPercent
            insiderHoldingPercent
            buyCount
            sellCount
          }
          currentPrice {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          currentMarketCap {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          currentTotalVolume {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
        }
      }
      standardizedPerpetualsByCategory {
        category
        marketType
        pageInfo {
          hasNextPage
          hasPreviousPage
        }
        edges {
          id
          standardizedInstrumentId
          currency {
            asset {
              symbol
              name
            }
            decimals
            logoUrl
          }
          verified
          currentPrice {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          currentTotalVolume {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          leverage
        }
      }
      standardizedPredictionsByCategory {
        category
        marketType
        pageInfo {
          hasNextPage
          hasPreviousPage
        }
        edges {
          id
          standardizedInstrumentId
          slug
          title
          imageUrl
          volume {
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          closesAt
          eventType
          isSeries
          sportMetadata {
            isLive
            gameStatus
            sportType
            league
            scheduledStartTime
            homeTeamScore
            awayTeamScore
            period
            elapsed
            homeTeam {
              name
              abbreviation
              logo
              color
              record
            }
            awayTeam {
              name
              abbreviation
              logo
              color
              record
            }
            homeTeamWin {
              externalTokenId
              label
              color
              price {
                end {
                  timestamp
                  value
                }
              }
            }
            awayTeamWin {
              externalTokenId
              label
              color
              price {
                end {
                  timestamp
                  value
                }
              }
            }
            draw {
              externalTokenId
              label
              color
              price {
                end {
                  timestamp
                  value
                }
              }
            }
          }
          markets {
            question
            groupItemTitle
            sportsMarketType
            outcomes {
              externalTokenId
              label
              color
              price {
                end {
                  timestamp
                  value
                }
              }
            }
          }
        }
      }
    }
  }
  ${STANDARDIZED_FUNGIBLE_TOKEN_ANCHOR_FIELDS}
  ${STANDARDIZED_PREDICTION_EVENT_NAVIGATION_FIELDS}
`;
export const SearchQuery = gql `
  query SearchQuery($input: SearchRequest!) {
    search(input: $input) {
      standardizedFungibleTokensByCategory {
        category
        marketType
        pageInfo {
          hasNextPage
          hasPreviousPage
        }
        edges {
          id
          standardizedInstrumentId
          currency {
            asset {
              symbol
              name
            }
            decimals
            logoUrl
          }
          verified
          anchorToken {
            address
            platformId
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
          }
          metadata {
            socials {
              platform
              url
            }
            description
            createdAt
            launchpad
            lifecycleStage
            bondingCurveProgress
            holders
            txCount
            uniqueTraders
            confidenceScore
            devHoldingPercent
            sniperHoldingPercent
            top10HoldersPercent
            bundleHoldingPercent
            insiderHoldingPercent
            buyCount
            sellCount
          }
          currentPrice {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          currentMarketCap {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          currentTotalVolume {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
        }
      }
      standardizedPerpetualsByCategory {
        category
        marketType
        pageInfo {
          hasNextPage
          hasPreviousPage
        }
        edges {
          id
          standardizedInstrumentId
          currency {
            asset {
              symbol
              name
            }
            decimals
            logoUrl
          }
          verified
          currentPrice {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          currentTotalVolume {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          leverage
        }
      }
      standardizedPredictionsByCategory {
        category
        marketType
        pageInfo {
          hasNextPage
          hasPreviousPage
        }
        edges {
          id
          standardizedInstrumentId
          slug
          title
          imageUrl
          volume {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          closesAt
          markets {
            question
            description
            resolutionSource
            imageUrl
            groupItemTitle
            sportsMarketType
            startDate
            endDate
            status
            volume {
              start {
                timestamp
                value
              }
              end {
                timestamp
                value
              }
              changeInPercentage
            }
            outcomes {
              externalTokenId
              label
              # imageUrl
              color
              price {
                start {
                  timestamp
                  value
                }
                end {
                  timestamp
                  value
                }
                changeInPercentage
              }
            }
          }
        }
      }
      walletProfiles {
        accounts {
          id
          address
          vmType
          type
          platformId
        }
        displayName
        bio
        xHandle
        avatarUrl
        verified
        followerCount
        followingCount
        joinedAt
        labels
        isFollowedByViewer
      }
    }
  }
`;
export const GetPositionsQuery = gql `
  query GetPositionsQuery($input: GetPositionsRequest!) {
    getPositions(input: $input) {
      fungibleTokenPositions {
        positions {
          standardizedInstrumentId
          verified
          tokenBalancesOnPlatforms {
            amount
            value
            token {
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
              address
              platformId
            }
            amountInFiatCurrency {
              value
              amount
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
            }
          }
          totalValueInFiat {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          total {
            amount
            value
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
          }
        }
        totalValueInFiat {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
      }
      perpetualPositions {
        crossMarginPositions {
          venue
          positions {
            standardizedInstrumentId
            side
            size {
              amount
              value
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
            }
            entryPrice {
              amount
              value
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
            }
            markPrice {
              amount
              value
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
            }
            unrealizedPnlUsd {
              amount
              value
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
            }
            leverage
            leverageType
            liquidationPrice {
              amount
              value
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
            }
          }
          marginSummary {
            equityUsd {
              amount
              value
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
            }
            freeMarginUsd {
              amount
              value
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
            }
            marginUsedUsd {
              amount
              value
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
            }
          }
          totalValueInFiat {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
        }
        isolatedMarginPositions {
          venue
          position {
            standardizedInstrumentId
            side
            size {
              amount
              value
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
            }
            entryPrice {
              amount
              value
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
            }
            unrealizedPnlUsd {
              amount
            }
            leverage
          }
          marginSummary {
            equityUsd {
              amount
              value
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
            }
          }
          totalValueInFiat {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
        }
        totalValueInFiat {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
      }
      predictionPositions {
        positions {
          standardizedInstrumentId
          avgEntryPrice
          eventTitle
          outcomeIndex
          pnl {
            amount
            value
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
          }
          pnlPercent
          size
          market {
            question
            imageUrl
            groupItemTitle
            status
            outcomes {
              label
              color
              externalTokenId
              price {
                start {
                  timestamp
                  value
                }
                end {
                  timestamp
                  value
                }
                changeInPercentage
              }
            }
          }
        }
        totalValueInFiat {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
      }
      stablecoinPositions {
        positions {
          standardizedInstrumentId
          verified
          tokenBalancesOnPlatforms {
            amount
            value
            token {
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
              address
              platformId
            }
            amountInFiatCurrency {
              value
              amount
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
            }
          }
          totalValueInFiat {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          total {
            amount
            value
            currency {
              asset {
                symbol
                name
              }
              decimals
              logoUrl
            }
          }
        }
        totalValueInFiat {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
      }
      totalValueInFiat {
        start {
          timestamp
          value
        }
        end {
          timestamp
          value
        }
        changeInPercentage
      }
    }
  }
`;
export const GetPortfolioValueHistoryQuery = gql `
  query GetPortfolioValueHistoryQuery($input: GetPortfolioValueHistoryRequest!) {
    getPortfolioValueHistory(input: $input) {
      timeframe
      data {
        ...PortfolioRailSeries
      }
      tokensData {
        ...PortfolioRailSeries
      }
      perpsData {
        ...PortfolioRailSeries
      }
      predictionsData {
        ...PortfolioRailSeries
      }
      stablecoinsData {
        ...PortfolioRailSeries
      }
    }
  }

  fragment PortfolioRailSeries on MarketDataTimeSeriesAndSummary {
    price {
      timestamp
      value
    }
    priceOHLC {
      timestamp
      open
      high
      low
      close
    }
    priceChangeInPercentage
    summary {
      sells {
        numberOfTransactions
        totalVolume
        numberOfUniqueAccounts
      }
      buys {
        numberOfTransactions
        totalVolume
        numberOfUniqueAccounts
      }
    }
  }
`;
export const GetActivityHistoryQuery = gql `
  query GetActivityHistoryQuery($input: GetActivityHistoryRequest!) {
    getActivityHistory(input: $input) {
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
      edges {
        instrumentType
        tradeItem {
          ... on Trade {
            accountAddress
            amount {
              token {
                currency {
                  asset {
                    symbol
                    name
                  }
                  decimals
                  logoUrl
                }
                address
                chainId
                platformId
              }
              value
              amount
              amountInFiatCurrency {
                currency {
                  asset {
                    symbol
                    name
                  }
                  decimals
                  logoUrl
                }
                value
                amount
              }
            }
            balanceImpact
            fiatPnl {
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
              value
              amount
            }
            timestamp
          }
          ... on PerpTrade {
            venue
            symbol
            tradeId
            side
            price {
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
              value
              amount
            }
            size {
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
              value
              amount
            }
            value {
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
              value
              amount
            }
            closedPnl {
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
              value
              amount
            }
            positionBefore {
              currency {
                asset {
                  symbol
                  name
                }
                decimals
                logoUrl
              }
              value
              amount
            }
            leverage
            timestamp
          }
          ... on PredictionTradeItem {
            predTimestamp: timestamp
            predOutcome: outcome {
              externalTokenId
              label
              color
            }
            predSide: side
            quantity
            predPrice: price
            fiatValue
            predBalanceImpact: balanceImpact
            pnl
            pnlPercent
            eventTitle
            transactionHash
          }
          ... on PredictionOrder {
            orderVenue: venue
            orderId
            orderOutcome: outcome {
              externalTokenId
              label
              color
            }
            orderSide: side
            orderPrice: price
            orderSize: size
            eventTitle
          }
        }
      }
    }
  }
`;
export const GetPerpOrderbookQuery = gql `
  query GetPerpOrderbookQuery($input: GetPerpOrderbookRequest!) {
    getPerpOrderbook(input: $input) {
      orderBook {
        dataTimestamp
        bids {
          price
          size
          numOrders
        }
        asks {
          price
          size
          numOrders
        }
      }
      tickOptions {
        aggregation {
          nSigFigs
          mantissa
        }
        tickSize
        label
      }
      spread
      spreadPercent
      midPrice
    }
  }
`;
/**
 * Prediction outcome orderbook. Cache key: (standardizedInstrumentId, marketId, outcomeTokenId).
 * Populated by instrumentOrderbookUpdates subscription in LiveUpdatesProvider;
 * initial fetch returns placeholder until CLOB feed is implemented.
 */
export const GetPredictionOrderbookQuery = gql `
  query GetPredictionOrderbookQuery($input: GetPredictionOrderbookRequest!) {
    getPredictionOrderbook(input: $input) {
      orderBook {
        dataTimestamp
        bids {
          price
          size
          numOrders
        }
        asks {
          price
          size
          numOrders
        }
      }
      tickOptions {
        aggregation {
          nSigFigs
          mantissa
        }
        tickSize
        label
      }
      spread
      spreadPercent
      midPrice
    }
  }
`;
/** Lightweight query for polling live price - uses field selection to only fetch currentPrice */
export const GetPerpetualLivePriceQuery = gql `
  query GetPerpetualLivePriceQuery($input: GetStandardizedPerpetualRequest!) {
    getStandardizedPerpetual(input: $input) {
      standardizedInstrumentId
      currentPrice {
        start {
          timestamp
          value
        }
        end {
          timestamp
          value
        }
        changeInPercentage
      }
    }
  }
`;
// ---------------------------------------------------------------------------
// Lightweight chart-only queries — fetch only oneDay price time series
// Used by hot markets carousel to lazily load charts per-card.
// ---------------------------------------------------------------------------
export const GetFungibleTokenOneDayChartQuery = gql `
  query GetFungibleTokenOneDayChartQuery($input: GetStandardizedFungibleTokenRequest!) {
    getStandardizedFungibleToken(input: $input) {
      id
      standardizedInstrumentId
      marketData {
        chartData {
          oneDay {
            price {
              timestamp
              value
            }
          }
        }
      }
    }
  }
`;
export const GetPerpetualOneDayChartQuery = gql `
  query GetPerpetualOneDayChartQuery($input: GetStandardizedPerpetualRequest!) {
    getStandardizedPerpetual(input: $input) {
      id
      standardizedInstrumentId
      markets {
        chartData {
          oneDay {
            price {
              timestamp
              value
            }
          }
        }
      }
    }
  }
`;
export const GetPredictionOneDayChartQuery = gql `
  query GetPredictionOneDayChartQuery($input: GetStandardizedPredictionEventRequest!) {
    getStandardizedPredictionEvent(input: $input) {
      id
      standardizedInstrumentId
      markets {
        outcomes {
          externalTokenId
          label
          price {
            end {
              value
            }
          }
          chartData {
            oneDay {
              price {
                timestamp
                value
              }
            }
          }
        }
      }
    }
  }
`;
export const GetTopTradersQuery = gql `
  query GetTopTraders($input: GetTopTradersRequest!) {
    getTopTraders(input: $input) {
      pageInfo {
        totalCount
        hasNextPage
        hasPreviousPage
      }
      traders {
        address
        buyCount
        sellCount
        buyAmount
        sellAmount
        buyVolumeUsd
        sellVolumeUsd
        pnlUsd
        remainingTokens
        lastActive
      }
    }
  }
`;
export const GetTopHoldersQuery = gql `
  query GetTopHolders($input: GetTopHoldersRequest!) {
    getTopHolders(input: $input) {
      pageInfo {
        totalCount
        hasNextPage
        hasPreviousPage
      }
      holders {
        address
        buyCount
        sellCount
        buyAmount
        sellAmount
        buyVolumeUsd
        sellVolumeUsd
        pnlUsd
        remainingTokens
        lastActive
      }
    }
  }
`;
export const GetTopPredictionTradersQuery = gql `
  query GetTopPredictionTraders($input: GetTopPredictionTradersRequest!) {
    getTopPredictionTraders(input: $input) {
      pageInfo {
        totalCount
        hasNextPage
        hasPreviousPage
      }
      traders {
        address
        buyCount
        sellCount
        buyVolumeUsd
        sellVolumeUsd
        pnlUsd
        remainingUsd
        lastActive
      }
    }
  }
`;
export const GetTopPredictionHoldersQuery = gql `
  query GetTopPredictionHolders($input: GetTopPredictionHoldersRequest!) {
    getTopPredictionHolders(input: $input) {
      pageInfo {
        totalCount
        hasNextPage
        hasPreviousPage
      }
      holders {
        address
        buyCount
        sellCount
        buyVolumeUsd
        sellVolumeUsd
        pnlUsd
        remainingUsd
        lastActive
      }
    }
  }
`;
export const GetRecentPerpTradesQuery = gql `
  ${PerpTradeFields}
  query GetRecentPerpTrades($input: GetRecentPerpTradesRequest!) {
    getRecentPerpTrades(input: $input) {
      trades {
        ...PerpTradeFields
      }
    }
  }
`;
export const GetRecentPredictionTradesQuery = gql `
  query GetRecentPredictionTrades($input: GetRecentPredictionTradesRequest!) {
    getRecentPredictionTrades(input: $input) {
      trades {
        timestamp
        source
        outcomeTokenId
        conditionId
        outcomeName
        side
        size
        price
        volumeUsd
        txHash
        traderAddress
      }
    }
  }
`;
export const GetPredictionBiggestMoversQuery = gql `
  query GetPredictionBiggestMovers($input: GetPredictionBiggestMoversRequest!) {
    getPredictionBiggestMovers(input: $input) {
      movers {
        outcomeTokenId
        conditionId
        title
        currentPrice
        priceChange
      }
    }
  }
`;
export const GetRecentGlobalPredictionTradesQuery = gql `
  query GetRecentGlobalPredictionTrades($input: GetGlobalPredictionTradesRequest!) {
    getRecentGlobalPredictionTrades(input: $input) {
      trades {
        timestamp
        outcomeTokenId
        conditionId
        traderAddress
        side
        size
        price
        volumeUsd
        eventTitle
      }
    }
  }
`;
export const GetGlobalTopPredictionTradersQuery = gql `
  query GetGlobalTopPredictionTraders($input: GetGlobalPredictionTradesRequest!) {
    getGlobalTopPredictionTraders(input: $input) {
      traders {
        traderAddress
        buyCount
        sellCount
        buyVolumeUsd
        sellVolumeUsd
        totalVolumeUsd
        tradeCount
        lastActive
      }
    }
  }
`;
export const GetLargestPredictionTradesQuery = gql `
  query GetLargestPredictionTrades($input: GetGlobalPredictionTradesRequest!) {
    getLargestPredictionTrades(input: $input) {
      trades {
        timestamp
        outcomeTokenId
        conditionId
        traderAddress
        side
        size
        price
        volumeUsd
        eventTitle
      }
    }
  }
`;
export const GetPredictionTradeSurgeQuery = gql `
  query GetPredictionTradeSurge($input: GetGlobalPredictionTradesRequest!) {
    getPredictionTradeSurge(input: $input) {
      events {
        conditionId
        eventTitle
        recentVol
        avgDailyVol
        surgeRatio
        currentPrice
      }
    }
  }
`;
export const GetContrarianPredictionTradersQuery = gql `
  query GetContrarianPredictionTraders($input: GetGlobalPredictionTradesRequest!) {
    getContrarianPredictionTraders(input: $input) {
      traders {
        traderAddress
        outcomeTokenId
        conditionId
        eventTitle
        side
        totalVolumeUsd
        tradeCount
        currentPrice
      }
    }
  }
`;
export const GetPredictionEventCommentsQuery = gql `
  query GetPredictionEventComments($input: GetPredictionEventCommentsRequest!) {
    getPredictionEventComments(input: $input) {
      comments {
        id
        body
        username
        profileImage
        userAddress
        createdAt
        reactionCount
        parentCommentId
      }
    }
  }
`;
export const GetTopPerpTradersQuery = gql `
  query GetTopPerpTraders($input: GetTopPerpTradersRequest!) {
    getTopPerpTraders(input: $input) {
      pageInfo {
        totalCount
        hasNextPage
        hasPreviousPage
      }
      traders {
        address
        buyCount
        sellCount
        buyAmount
        sellAmount
        buyVolumeUsd
        sellVolumeUsd
        pnlUsd
        remainingTokens
        lastActive
      }
    }
  }
`;
// ===========================================================================
// Wallet profile (Profile screen)
// ===========================================================================
export const GetWalletProfileQuery = gql `
  query GetWalletProfile($input: GetWalletProfileInput!) {
    getWalletProfile(input: $input) {
      accounts {
        id
        address
        vmType
        type
        platformId
      }
      displayName
      bio
      xHandle
      avatarUrl
      verified
      followerCount
      followingCount
      joinedAt
      labels
      isFollowedByViewer
    }
  }
`;
export const GetWalletMetricsQuery = gql `
  query GetWalletMetrics($accountId: String!, $timeframe: ChartTimeframe) {
    getWalletMetrics(accountId: $accountId, timeframe: $timeframe) {
      tokens {
        avgHoldSeconds
        sellCount
        openLotCount
        closedLotCount
      }
      perps {
        avgHoldSeconds
        closedPositionCount
        openPositionCount
        tradeCount
        winRate
      }
      predictions {
        avgHoldSeconds
        closedPositionCount
        openPositionCount
        tradeCount
        winRate
        resolvedMarketCount
      }
    }
  }
`;
export const GetPerformanceQuery = gql `
  query GetPerformance($input: GetPerformanceInput!) {
    getPerformance(input: $input) {
      tokens {
        topWins {
          displayName
          realizedPnlUsd
          positionCount
          standardizedInstrumentId
          instrumentType
          categoryId
          categoryName
          parentCategoryId
        }
        topLosses {
          displayName
          realizedPnlUsd
          positionCount
          standardizedInstrumentId
          instrumentType
          categoryId
          categoryName
          parentCategoryId
        }
      }
      perps {
        topWins {
          displayName
          realizedPnlUsd
          positionCount
          standardizedInstrumentId
          instrumentType
        }
        topLosses {
          displayName
          realizedPnlUsd
          positionCount
          standardizedInstrumentId
          instrumentType
        }
      }
      predictions {
        topWins {
          displayName
          realizedPnlUsd
          positionCount
          standardizedInstrumentId
          instrumentType
        }
        topLosses {
          displayName
          realizedPnlUsd
          positionCount
          standardizedInstrumentId
          instrumentType
        }
      }
      pnlCompleteness
    }
  }
`;
// ===========================================================================
// Signal tab feed
// ===========================================================================
export const ListSignalsQuery = gql `
  query ListSignals($input: ListSignalsInput!) {
    listSignals(input: $input) {
      pageInfo {
        totalCount
        hasNextPage
        hasPreviousPage
      }
      edges {
        id
        instrumentType
        convictionMultiple
        profile {
          ...SignalFeedProfileFields
        }
        instrument {
          ...SignalFeedInstrumentFields
        }
        activityItem {
          ...SignalFeedActivityItemFields
        }
      }
    }
  }
  ${SIGNAL_FEED_PROFILE_FIELDS}
  ${SIGNAL_FEED_INSTRUMENT_FIELDS}
  ${SIGNAL_FEED_ACTIVITY_ITEM_FIELDS}
`;
export const GetClosedPositionsQuery = gql `
  query GetClosedPositions($input: GetPositionsRequest!) {
    getPositions(input: $input) {
      closedPositions {
        positionUid
        standardizedInstrumentId
        instrumentType
        displayName
        venue
        openedAt
        closedAt
        costBasisUsd
        proceedsUsd
        realizedPnlUsd
      }
    }
  }
`;
// ===========================================================================
// Leaderboard tab
// ===========================================================================
export const ListLeaderboardQuery = gql `
  query ListLeaderboard($input: ListLeaderboardInput!) {
    listLeaderboard(input: $input) {
      pageInfo {
        totalCount
        hasNextPage
        hasPreviousPage
      }
      edges {
        rank
        totalPnlUsd
        totalTrades
        totalVolumeUsd
        profile {
          accounts {
            id
            address
            vmType
            type
            platformId
          }
          displayName
          avatarUrl
          xHandle
          verified
        }
      }
    }
  }
`;
export const GetInstrumentCategoriesQuery = gql `
  query GetInstrumentCategories($input: GetInstrumentCategoriesInput) {
    getInstrumentCategories(input: $input) {
      categories {
        id
        name
        slug
        parentId
        iconUrl
        displayOrder
      }
    }
  }
`;
export const GetSparklineBatchQuery = gql `
  query GetSparklineBatch($input: GetSparklineBatchRequest!) {
    getSparklineBatch(input: $input) {
      timeframe
      entries {
        id
        points
      }
    }
  }
`;
